import React, { createContext, useCallback, useContext, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useAuth } from './AuthContext';
import { useCompetitions } from '../components/CompetitionContext';
import {
  ANALYTICS_CONSENT_EVENT, ANALYTICS_CONSENT_KEY, analyticsPath, hasAnalyticsConsent,
  pageContext, readSalesContext, rememberSalesContext, setCurrentAnalyticsContext,
  syncAnalyticsConsent, flushConsentRevocations,
  trackAnalyticsEvent, trackAnalyticsPage,
} from '../utils/analytics';

const AnalyticsContext = createContext(null);
const isSalesPath = (path) => /^\/(vip|kup-vip|login|register|billing(?:\/|$)|konto(?:\/|$))/.test(path);

export function AnalyticsProvider({ children }) {
  const location = useLocation();
  const { user, loading, hasRole } = useAuth();
  const { competitions, loading: competitionsLoading } = useCompetitions();
  const [consent, setConsent] = useState(hasAnalyticsConsent);
  const [contextRevision, setContextRevision] = useState(0);
  const [pageDetails, setPageDetails] = useState(null);
  const seen = useRef(new Set());
  const pageKey = useRef(null);
  const current = { ...pageContext(location.pathname, competitions),
    ...(pageDetails?.key === location.key ? pageDetails.params : {}) };
  const sales = isSalesPath(location.pathname) ? readSalesContext() : {};
  const context = { ...current, ...sales, page_type: current.page_type,
    auth_state: user ? 'logged_in' : 'anonymous',
    access_tier: hasRole('admin') ? 'admin'
      : hasRole('poland') || hasRole('poland_regions') || hasRole('vip') ? 'vip' : 'free' };
  if (location.pathname === '/kup-vip') {
    context.plan = 'vip';
    context.billing_period = new URLSearchParams(location.search).get('interval') === 'yearly' ? 'yearly' : 'monthly';
  }
  const contextJson = JSON.stringify(context);
  const contextRef = useRef(context);
  const navigationKey = useRef(location.key);
  const suppressed = location.pathname.startsWith('/janekjot');
  const ready = consent && !loading && !suppressed
    && (!/^\/liga\//.test(location.pathname) || !competitionsLoading);

  useEffect(() => {
    const update = () => {
      setConsent(hasAnalyticsConsent());
      setContextRevision((value) => value + 1);
      if (!hasAnalyticsConsent()) { seen.current.clear(); pageKey.current = null; }
    };
    const storage = (event) => {
      if (event.key === null || event.key === ANALYTICS_CONSENT_KEY || event.key.startsWith('symuliga_sales_')) {
        syncAnalyticsConsent();
        update();
      }
    };
    flushConsentRevocations();
    window.addEventListener(ANALYTICS_CONSENT_EVENT, update);
    window.addEventListener('storage', storage);
    window.addEventListener('online', flushConsentRevocations);
    return () => {
      window.removeEventListener(ANALYTICS_CONSENT_EVENT, update);
      window.removeEventListener('storage', storage);
      window.removeEventListener('online', flushConsentRevocations);
    };
  }, []);

  useLayoutEffect(() => {
    if (navigationKey.current !== location.key) {
      navigationKey.current = location.key;
      seen.current.clear();
    }
    contextRef.current = JSON.parse(contextJson);
    setCurrentAnalyticsContext(contextRef.current);
  }, [contextJson, contextRevision, location.key]);

  const registerPageDetails = useCallback((params) => {
    setPageDetails({ key: location.key, params });
  }, [location.key]);

  const trackOnce = useCallback((name, params = {}) => {
    const identity = `${location.key}:${name}:${params.feature || ''}:${params.requirement || ''}:${params.placement || ''}`;
    if (!ready || seen.current.has(identity)) return;
    if (trackAnalyticsEvent(name, params)) {
      if (seen.current.size > 500) seen.current.clear();
      seen.current.add(identity);
    }
  }, [location.key, ready]);

  useEffect(() => {
    if (!ready || pageKey.current === location.key) return;
    pageKey.current = location.key;
    trackAnalyticsPage(`${location.pathname}${location.search}`);
    if (location.pathname === '/vip') trackOnce('vip_page_view');
    if (location.pathname === '/kup-vip') trackOnce('checkout_view');
    if (location.pathname === '/billing/cancel') trackOnce('checkout_cancel');
  }, [ready, location.key, location.pathname, location.search, trackOnce]);

  useEffect(() => {
    const onClick = (event) => {
      if (suppressed || !hasAnalyticsConsent()) return;
      const anchor = event.target.closest?.('a[href]');
      if (!anchor) return;
      const url = new URL(anchor.href, window.location.origin);
      if (url.origin !== window.location.origin || !['/vip', '/kup-vip'].includes(url.pathname)) return;
      const placement = anchor.closest('[data-analytics-placement]')?.getAttribute('data-analytics-placement') || 'content';
      const params = { ...contextRef.current, placement,
        ...(placement === 'navigation' && !isSalesPath(location.pathname)
          ? { source_feature: 'navigation', source_path: analyticsPath(location.pathname) } : {}) };
      if (!isSalesPath(location.pathname) || !readSalesContext().source_feature) {
        params.source_path = analyticsPath(location.pathname);
      }
      rememberSalesContext(params);
      trackAnalyticsEvent('vip_cta_click', { ...params,
        ...(url.pathname === '/kup-vip' ? { plan: 'vip',
          billing_period: url.searchParams.get('interval') === 'yearly' ? 'yearly' : 'monthly' } : {}) });
      setContextRevision((value) => value + 1);
    };
    document.addEventListener('click', onClick, true);
    return () => document.removeEventListener('click', onClick, true);
  }, [location.pathname, suppressed]);

  return <AnalyticsContext.Provider value={{ ready, context, trackOnce, registerPageDetails }}>{children}</AnalyticsContext.Provider>;
}

export function useAnalyticsPageContext(params) {
  const registerPageDetails = useContext(AnalyticsContext)?.registerPageDetails;
  const paramsJson = JSON.stringify(params);
  useLayoutEffect(() => {
    if (registerPageDetails) registerPageDetails(JSON.parse(paramsJson));
  }, [registerPageDetails, paramsJson]);
}

// One visible exposure per feature/placement/navigation, including React StrictMode.
export function useAnalyticsExposure(names, params = {}, enabled = true) {
  const analytics = useContext(AnalyticsContext);
  const ready = analytics?.ready;
  const trackOnce = analytics?.trackOnce;
  const ref = useRef(null);
  const eventsJson = JSON.stringify(Array.isArray(names) ? names : [names]);
  const paramsJson = JSON.stringify(params);
  useEffect(() => {
    const target = ref.current;
    if (!target || !enabled || !ready) return undefined;
    let timer;
    const emit = () => {
      if (document.visibilityState === 'hidden') return;
      JSON.parse(eventsJson).forEach((name) => trackOnce(name, JSON.parse(paramsJson)));
    };
    if (!window.IntersectionObserver) { emit(); return undefined; }
    const observer = new IntersectionObserver(([entry]) => {
      window.clearTimeout(timer);
      if (entry.isIntersecting && entry.intersectionRatio >= 0.25) timer = window.setTimeout(emit, 500);
    }, { threshold: [0, 0.25] });
    observer.observe(target);
    return () => { observer.disconnect(); window.clearTimeout(timer); };
  }, [ready, trackOnce, eventsJson, paramsJson, enabled]);
  return ref;
}
